/**
 * Your picks: the exercises collected while browsing, on their way to a sheet.
 *
 * Replaces the bar that only said "3 exercises selected". You could not see what you had
 * picked, put them in order, or drop one, so a sheet was built blind and fixed up in the
 * editor afterwards. The tray shows each pick with its time and whether it has a
 * solution, adds them up, and turns them into a new sheet or adds them to an existing one.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, FilePlus2, Layers, ListPlus, Trash2, X } from 'lucide-react';
import { isArchiveId, unescapeTex, type ExerciseDetail } from '@studio/shared';
import { api, type SheetSummaryWithTerm } from '../api';
import { useBasket } from '../basket';
import { exerciseInfo } from '../exerciseCache';
import { L, sheetDisplayTitle } from '../labels';
import { Button, IconButton, Spinner, TimeBadge, useEscapeLayer, useFocusTrap } from './ui';
import { NewSheetForm } from './NewSheetForm';
import { useToast } from './Toast';
import { tx, txn } from '../i18n';

export function PicksTray() {
  const basket = useBasket();
  const { trayOpen, setTrayOpen } = basket;
  const navigate = useNavigate();
  const { push } = useToast();
  const [info, setInfo] = useState<Record<string, ExerciseDetail | null>>({});
  const [creating, setCreating] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [sheets, setSheets] = useState<SheetSummaryWithTerm[] | null>(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  useEscapeLayer(trayOpen && !creating, () => setTrayOpen(false), { modal: true });
  const panel = useRef<HTMLElement>(null);
  useFocusTrap(panel, trayOpen && !creating);
  // going somewhere — the sheet just made, a link — closes the tray
  const { pathname } = useLocation();
  useEffect(() => setTrayOpen(false), [pathname, setTrayOpen]);

  // each pick is asked for once; a failed one is asked again the next time the tray opens
  const requested = useRef(new Set<string>());
  useEffect(() => {
    if (!trayOpen) return;
    for (const id of basket.ids) {
      if (requested.current.has(id)) continue;
      requested.current.add(id);
      void exerciseInfo(id).then((d) => {
        if (!d) requested.current.delete(id);
        setInfo((prev) => ({ ...prev, [id]: d }));
      });
    }
  }, [trayOpen, basket.ids]);

  const totals = useMemo(() => {
    let minutes = 0;
    let withSolution = 0;
    for (const id of basket.ids) {
      const d = info[id];
      if (!d) continue;
      minutes += d.timeMinutes ?? 0;
      if (d.hasSolution) withSolution++;
    }
    return { minutes, withSolution };
  }, [basket.ids, info]);

  const move = (i: number, d: -1 | 1) => {
    const next = [...basket.ids];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    basket.reorder(next);
  };

  const clear = () => {
    const before = basket.ids;
    basket.clear();
    push(txn(before.length, 'Cleared {n} pick', 'Cleared {n} picks'), 'info', { action: { label: tx('Undo'), onClick: () => basket.reorder(before) } });
  };

  const addTo = async (sheet: SheetSummaryWithTerm) => {
    setBusy(true);
    try {
      const count = basket.ids.length;
      /*
       * Appended through the CRDT on the server; bodies are filled in there too. On a course
       * sheet a 2025 exercise goes in as its Typst version where the 2026 archive has one,
       * as the editor's Add tab places it (the server knows which sheet is one).
       */
      await api.appendItems(
        sheet.id,
        basket.ids.map((exerciseId) => ({ kind: 'exercise', exerciseId, includeSolution: true })),
        { typst: true },
      );
      basket.clear();
      setChoosing(false);
      setTrayOpen(false);
      setSheets(null);
      push(txn(count, 'Added {n} exercise to “{sheet}”', 'Added {n} exercises to “{sheet}”', { sheet: sheetDisplayTitle(sheet) }), 'success', {
        action: { label: tx('Open'), onClick: () => navigate(`/sheets/${sheet.id}`) },
      });
    } catch (err) {
      push(err instanceof Error ? err.message : tx('Could not add the exercises'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const visibleSheets = (sheets ?? []).filter((s) =>
    `${s.label} ${sheetDisplayTitle(s)}`.toLowerCase().includes(filter.trim().toLowerCase()),
  );

  return (
    <>
      {/* the way back to the tray from anywhere, once there is something in it */}
      {!trayOpen && basket.ids.length > 0 && (
        <button
          className="btn btn-accent fixed bottom-4 left-1/2 z-40 -translate-x-1/2 shadow-lg"
          onClick={() => setTrayOpen(true)}
        >
          <Layers size={15} /> {L.picks} ({basket.ids.length})
        </button>
      )}

      {trayOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onMouseDown={(e) => e.target === e.currentTarget && setTrayOpen(false)}>
          <aside
            ref={panel}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={L.picks}
            className="flex h-full w-[min(26rem,100vw)] flex-col border-l border-[var(--line)] bg-[var(--surface)] shadow-xl"
          >
            <header className="flex items-center justify-between gap-2 border-b border-[var(--line)] px-4 py-3">
              <div>
                <h2 className="text-sm font-semibold">
                  {L.picks} ({basket.ids.length})
                </h2>
                {basket.ids.length > 0 && (
                  <p className="text-xs text-[var(--ink-soft)]">
                    {tx('about {minutes} min · {withSolution} of {total} with a solution', {
                      minutes: totals.minutes,
                      withSolution: totals.withSolution,
                      total: basket.ids.length,
                    })}
                  </p>
                )}
              </div>
              <IconButton label={tx('Close')} onClick={() => setTrayOpen(false)}>
                <X size={16} />
              </IconButton>
            </header>

            <div className="scroll-y min-h-0 flex-1">
              {basket.ids.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-[var(--ink-soft)]">
                  {tx('Nothing picked yet. Use “{addToPicks}” on any exercise on the {exercises} page or on a past sheet.', {
                    addToPicks: L.addToPicks,
                    exercises: L.exercises,
                  })}
                </p>
              ) : (
                <ol className="divide-y divide-[var(--line)]">
                  {basket.ids.map((id, i) => {
                    const d = info[id];
                    return (
                      <li key={id} className="flex items-center gap-2 px-3 py-2">
                        <span className="w-5 shrink-0 text-right text-xs text-[var(--ink-faint)]">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {d === undefined ? <Spinner size={12} /> : d ? unescapeTex(d.title ?? '') || d.printedNumber || id : id}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[var(--ink-faint)]">
                            {d && (
                              <span>
                                {d.sheetLabel
                                  ? tx('{year} · sheet {label}', { year: d.year, label: d.sheetLabel })
                                  : isArchiveId(d.id)
                                    ? tx('2026 archive')
                                    : d.year}
                              </span>
                            )}
                            {d && <TimeBadge minutes={d.timeMinutes} />}
                            {d && !d.hasSolution && <span className="text-[var(--color-camel-600)]">{tx('no solution')}</span>}
                          </div>
                        </div>
                        <IconButton label={tx('Move up')} onClick={() => move(i, -1)} disabled={i === 0}>
                          <ArrowUp size={14} />
                        </IconButton>
                        <IconButton label={tx('Move down')} onClick={() => move(i, 1)} disabled={i === basket.ids.length - 1}>
                          <ArrowDown size={14} />
                        </IconButton>
                        <IconButton label={tx('Remove from picks')} onClick={() => basket.remove(id)}>
                          <X size={14} />
                        </IconButton>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {choosing && (
              <div className="max-h-[45vh] border-t border-[var(--line)] p-3">
                <input
                  className="input mb-2"
                  autoFocus
                  placeholder={tx('Find a sheet…')}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label={tx('Find a sheet')}
                />
                <ul className="scroll-y max-h-[30vh] divide-y divide-[var(--line)] rounded-lg border border-[var(--line)]">
                  {!sheets && (
                    <li className="flex justify-center py-4">
                      <Spinner />
                    </li>
                  )}
                  {visibleSheets.map((s) => (
                    <li key={s.id}>
                      <button
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--surface-muted)]"
                        disabled={busy || s.myRole === 'viewer'}
                        onClick={() => void addTo(s)}
                      >
                        <span className="truncate">
                          <span className="chip chip-brand mr-2">{s.label}</span>
                          {sheetDisplayTitle(s)}
                        </span>
                        <span className="shrink-0 text-xs text-[var(--ink-faint)]">
                          {s.termYear ?? ''} · {txn(s.itemCount, '{n} item', '{n} items')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <footer className="flex flex-wrap items-center gap-2 border-t border-[var(--line)] px-4 py-3">
              <Button variant="primary" disabled={!basket.ids.length} onClick={() => setCreating(true)}>
                <FilePlus2 size={15} /> {L.makeSheetFromPicks}
              </Button>
              <Button
                disabled={!basket.ids.length}
                onClick={() => {
                  const opening = !choosing;
                  setChoosing(opening);
                  // fresh each time: item counts change, and a failed load gets another try
                  if (opening) {
                    setSheets(null);
                    void api.sheets().then(setSheets).catch(() => setSheets([]));
                  }
                }}
              >
                <ListPlus size={15} /> {L.addToSheet}
              </Button>
              <Button variant="ghost" className="ml-auto" disabled={!basket.ids.length} onClick={clear}>
                <Trash2 size={14} />{' '}{tx('Clear')}
              </Button>
            </footer>
          </aside>
        </div>
      )}

      <NewSheetForm
        open={creating}
        onClose={() => setCreating(false)}
        defaults={{ startFrom: 'picks' }}
      />
    </>
  );
}
